import React, { useEffect, useState, useCallback } from "react";
import { MdOutlineRemoveRedEye, MdEdit, MdContentCopy } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import TableColumn from "../../components/TableColumn/TableColumn";
import api from "../../services/api";
import { getIps, geoLabel } from "../../services/posthogGeo";
import reverseGeocode from "../../utils/reverseGeocode";
import "./Users.scss";
import { format } from "date-fns";
import Button from "../../components/Buttons/Button";
import profileDefault from "../../assets/images/profileDefault.png";

const USER_TYPE_LABELS = {
  venue_owner: { label: "Venue Owner", color: "#4F30EA" },
  advertiser: { label: "Advertiser", color: "#0A3FD6" },
  promoter: { label: "Promoter", color: "#F59E0B" },
  consumer: { label: "Consumer", color: "#6B7280" },
};

const renderUserTypes = (types) => {
  if (!types || types.length === 0) return "—";
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {types.map((t) => {
        const meta = USER_TYPE_LABELS[t] || { label: t, color: "#6B7280" };
        return (
          <span
            key={t}
            style={{
              background: meta.color,
              color: "#fff",
              fontSize: 11,
              fontWeight: 600,
              padding: "3px 8px",
              borderRadius: 999,
              whiteSpace: "nowrap",
            }}
          >
            {meta.label}
          </span>
        );
      })}
    </div>
  );
};

const Users = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(
    Number(localStorage.getItem("ListCurrentPageUsers")) || 1
  );

  const records = 10;
  const [searchTerm, setSearchTerm] = useState("");

  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState("null");

  const formatDate = (dateString) => {
    // Guard against missing/invalid dates (e.g. auto-provisioned stub
    // accounts with no last_active). An unguarded format() throws
    // "Invalid time value", which crashes the whole row-map and blanks
    // the entire table — not just the one cell.
    if (!dateString) return "—";
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? "—" : format(d, "MMMM d, yyyy");
  };

  const getUsers = useCallback(
      async (page) => {
        try {
          const response = await api.get(`/users`, {
            params: {
              page: page,
              records: records,
              sort_column: sortBy,
              sort: sortDirection,
              search: searchTerm, // Add search term
            },
          });

          const data = response.data.data;
          const itemsTableColumns = data.map((user) => ({
            Image: (
                <img
                    className="rounded"
                    style={{ height: 40, width: 40, objectFit: "cover" }}
                    src={user.photo_url || profileDefault}
                    alt="Moderator"
                />
            ),
            Name: `${user.firstName} ${user.lastName}`,
            "User Type": renderUserTypes(user.user_types),
            Device: user.app_platform === "ios" ? "iOS" : user.app_platform === "android" ? "Android" : "",
            Birthday: user.birthday,
            Gender: user.gender,
            "Member Since": formatDate(user.created_at),
            "Last Active": formatDate(user.last_active),
            Email: user.email,
            Phone: user.phone,
            Location: user.location || "—",
            IP: "…",
            "User ID": user._id ? (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard?.writeText(user._id);
                }}
                title={`Mongo _id: ${user._id}\n(click to copy)`}
                style={{
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: 12,
                  color: "rgba(255,255,255,0.6)",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {user._id}
                <MdContentCopy size={14} />
              </span>
            ) : "—",
            "Firebase ID": user.id ? (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard?.writeText(user.id);
                }}
                title={`Firebase UID: ${user.id}\n(click to copy)`}
                style={{
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: 12,
                  color: "rgba(255,255,255,0.6)",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {user.id}
                <MdContentCopy size={14} />
              </span>
            ) : "—",
            id: user.id,
          }));

          setUsers([...itemsTableColumns]);
          setTotalPages(response.data.total);

          // Enrich async: batch IP/geo-IP from PostHog + reverse-geocode the
          // stored lat/lng Location into a city. Both patch rows as they land.
          const uids = data.map((u) => u.id).filter(Boolean);
          getIps(uids).then((res) => {
            const gmap = res?.data || {};
            setUsers((prev) =>
              prev.map((row) => {
                const g = gmap[row.id];
                if (!g) return { ...row, IP: "—" };
                const label = geoLabel(g);
                return {
                  ...row,
                  IP: g.ip ? (
                    <span style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12, whiteSpace: "nowrap" }}>
                      {g.ip}
                      {label && (
                        <span style={{ display: "block", fontSize: 11, color: "rgba(255,255,255,0.5)" }}>{label}</span>
                      )}
                    </span>
                  ) : (label || "—"),
                };
              })
            );
          });
          data.forEach((u) => {
            if (!u.location) return;
            reverseGeocode(u.location).then((city) => {
              if (!city || city === u.location) return;
              setUsers((prev) => prev.map((row) => (row.id === u.id ? { ...row, Location: city } : row)));
            });
          });
        } catch (error) {
          console.log(`users get request didn't work. error: ${error}`);
        }
      },
      [sortBy, sortDirection, searchTerm] // Add searchTerm as a dependency
  );

  useEffect(() => {
    getUsers(currentPage);
  }, [currentPage, sortDirection, searchTerm, getUsers]);

  const fetchAllData = async () => {
    // Paginate. The previous version asked the API for `records: 9999999999`
    // which would either time out the server or overflow memory on a large
    // user collection. Pull in chunks of 500 until exhausted.
    const PAGE_SIZE = 500;
    const all = [];
    try {
      for (let page = 1; ; page += 1) {
        const response = await api.get(`/users`, {
          params: { page, records: PAGE_SIZE },
        });
        const batch = response?.data?.data || [];
        all.push(...batch);
        if (batch.length < PAGE_SIZE) break;
        if (page > 200) break; // hard cap: 100k users
      }

      // Image column intentionally omitted — xlsx can't serialize JSX, and
      // the URL alone is more useful in a spreadsheet than an empty cell.
      return all.map((user) => ({
        Name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        "User Type": (user.user_types || []).map((t) => USER_TYPE_LABELS[t]?.label || t).join(", "),
        Birthday: user.birthday,
        Gender: user.gender,
        "Member Since": user.created_at ? formatDate(user.created_at) : '',
        "Last Active": user.last_active ? formatDate(user.last_active) : '',
        Email: user.email || '',
        Phone: user.phone || '',
        Instagram: user.ig || '',
        Photo: user.photo_url || '',
        "User ID": user._id || '',
        "Firebase ID": user.id || '',
      }));
    } catch (error) {
      console.error(`Error fetching all data: ${error}`);
      window.alert(`Export failed: ${error?.response?.status || ''} ${error?.message || error}`);
      return [];
    }
  };

  const columns = [
    "Image",
    "Name",
    "Device",
    "Birthday",
    "Gender",
    "Member Since",
    "Last Active",
    "Email",
    "Phone",
    "Location",
    "IP",
  ];

  const sortableColumns = ["Name", "Birthday", "Gender", "Member Since", "Last Active"];

  const actions = [
    {
      label: <MdEdit size={30} color="#4F30EA" />,
      onClick: (rowData) => {
        navigate(`/users/edit/${rowData.id}`);
      },
    },
    {
      label: <MdOutlineRemoveRedEye size={30} color="#4F30EA" />,
      onClick: (rowData) => {
        navigate(`/users/${rowData.id}`);
      },
    },
  ];

  const handleChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    localStorage.setItem("ListCurrentPageUsers", page);
  };

  useEffect(() => {
    const storedPage = localStorage.getItem("ListCurrentPageUsers");
    setCurrentPage(Number(storedPage) || 1);
  }, []);

  const sortColumnsMapping = {
    Name: "firstName",
    Birthday: "birthday",
    Gender: "gender",
    "Member Since": "created_at",
    "Last Active": "last_active",
  };

  const handleSort = (columnName) => {
    const backendColumnName = sortColumnsMapping[columnName];
    if (sortBy === backendColumnName) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(backendColumnName);
      setSortDirection("asc");
    }
  };

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="users">
            <TableColumn
              title="Users"
              hasHeader={true}
              columns={columns}
              data={users}
              sortableColumns={sortableColumns}
              hasExport={true}
              hasSearch={true}
              actions={actions}
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              records={records}
              customButton={
                <Button primaryBtn onClick={() => navigate("/users/add")} style={{ paddingBottom: "18px", paddingTop: "18px" }}>
                  Add new user +
                </Button>
              }
              inputSearchProps={{ value: searchTerm, onChange: handleChange }}
              fetchAllData={fetchAllData}
              onSort={handleSort}
            />
          </section>
        </article>
      </div>
    </main>
  );
};

export default Users;
